import * as XLSX from "xlsx";

export function normalizeHeader(h) {
  return String(h ?? "")
    .trim()
    .replace(/\s+/g, " ");
}

function countNumericCells(row) {
  if (!Array.isArray(row)) return 0;
  return row.filter(
    (c) => typeof c === "number" && !Number.isNaN(c)
  ).length;
}

/**
 * Prefer sheet containing a "Main category" cell (Klimakompasset "Data (alle poster)").
 */
function pickSheetWithMainCategory(workbook) {
  for (const sheetName of workbook.SheetNames) {
    const sheet = workbook.Sheets[sheetName];
    const ref = sheet["!ref"];
    if (!ref) continue;
    const range = XLSX.utils.decode_range(ref);
    const maxR = Math.min(range.e.r, range.s.r + 120);
    for (let r = range.s.r; r <= maxR; r++) {
      for (let c = range.s.c; c <= range.e.c; c++) {
        const addr = XLSX.utils.encode_cell({ r, c });
        const cell = sheet[addr];
        const v = cell ? normalizeHeader(cell.v) : "";
        if (v.toLowerCase() === "main category") {
          return { sheetName, headerRowIndex: r };
        }
      }
    }
  }
  return null;
}

function pickSheetMostNumericRows(workbook) {
  let best = { sheetName: workbook.SheetNames[0], score: -1, headerRowIndex: 0 };
  for (const sheetName of workbook.SheetNames) {
    const sheet = workbook.Sheets[sheetName];
    const data = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "" });
    let numericRows = 0;
    for (const row of data) {
      if (countNumericCells(row) >= 2) numericRows += 1;
    }
    if (numericRows > best.score) {
      best = { sheetName, score: numericRows, headerRowIndex: 0 };
    }
  }
  return { sheetName: best.sheetName, headerRowIndex: best.headerRowIndex };
}

function makeUniqueHeaders(rawRow) {
  const seen = {};
  return rawRow.map((h, idx) => {
    let base = normalizeHeader(h) || `Column ${idx + 1}`;
    if (!seen[base]) {
      seen[base] = 1;
      return base;
    }
    seen[base] += 1;
    return `${base} (${seen[base]})`;
  });
}

/**
 * @param {ArrayBuffer} buffer
 * @returns {{ fileName: string, sheetName: string, headerRowIndex: number, headers: string[], rows: Record<string, unknown>[] }}
 */
export function parseSpreadsheetBuffer(buffer, fileName = "upload") {
  const workbook = XLSX.read(buffer, { type: "array" });
  const picked =
    pickSheetWithMainCategory(workbook) || pickSheetMostNumericRows(workbook);
  const sheet = workbook.Sheets[picked.sheetName];
  const matrix = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "" });
  const headerRowIndex = picked.headerRowIndex;
  const rawHeaderRow = matrix[headerRowIndex] || [];
  const headers = makeUniqueHeaders(rawHeaderRow);

  const rows = [];
  for (let i = headerRowIndex + 1; i < matrix.length; i++) {
    const line = matrix[i] || [];
    const obj = {};
    let empty = true;
    headers.forEach((key, j) => {
      const v = line[j];
      obj[key] = v;
      if (v !== "" && v != null) empty = false;
    });
    if (!empty) rows.push(obj);
  }

  return {
    fileName,
    sheetName: picked.sheetName,
    headerRowIndex,
    headers,
    rows,
  };
}

export function readSpreadsheetFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        resolve(parseSpreadsheetBuffer(e.target.result, file.name));
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(file);
  });
}

export function headerSignature(headers) {
  return [...headers].map(normalizeHeader).sort().join("|");
}
